import { useTranslation } from 'react-i18next';
import { useThemeStore } from './store';
import HeaderBar from './components/common/HeaderBar';
import LanguageSwitcher from './components/common/LanguageSwitcher';
import Section from './components/common/Section';
import LicenseKey from './LicenseKey';
import ResultsView from './components/ResultsView';
import PropertyGrid from './investor/PropertyGrid';
import { exportJSON } from './utils/ExportJSON';
import CompanyManager from './CompanyManager';
import { useCompanyStore } from './stores/company';
import { useAuthStore } from './stores/auth';
import SSOButton from './components/SSOButton';
import { useState, useMemo } from 'react';
import NavMenu, { NavItem } from './components/NavMenu';
import OraclePanel from './oracle/OraclePanel';
import IoTMeters from './iot/IoTMeters';
import GovBidPanel from './gov/GovBidPanel';
import FuturesDesk from './trading/FuturesDesk';
import VolatilityPanel from './market/VolatilityPanel';
import CBDCDesk from './sovereign/CBDCDesk';
import TaxonomyOraclePanel from './sovereign/TaxonomyOraclePanel';
import ComplianceSlasher from './sovereign/ComplianceSlasher';
import ComplianceSlasherV2 from './sovereign/ComplianceSlasherV2';
import ScopeRouterV2 from './sovereign/ScopeRouterV2';
import SmartOffsetV2 from './registry/SmartOffsetV2';
import NationalSyncV2 from './sovereign/NationalSyncV2';
import ScopeExchange from './routes/scopeExchange';
import ESeal from './routes/eSeal';
import CertificateNFT from './routes/certificateNFT';
import EUFabric from './routes/euFabric';
import EvidenceChain from './routes/evidenceChain';
import TenderMesh from './routes/tenderMesh';
import Tribunal from './routes/tribunal';
import ESGPassportRegistry from './routes/esgPassportRegistry';
import SubsidyEligibility from './routes/subsidyEligibility';
import SubsidyAllocation from './routes/subsidyAllocation';
import SovereignReconciliation from './routes/sovereignReconciliation';
import BeneficialGraph from './routes/beneficialGraph';
import JudicialTrigger from './routes/judicialTrigger';
import SatelliteOverlay from './routes/satelliteOverlay';
import AntiFraud from './routes/antiFraud';
import { SettlementPanel } from './panels/SettlementPanel';
import RegulatorExport from './export/RegulatorExport';
import TenderScoring from './sovereign/TenderScoring';
import ETSBridge from './sovereign/ETSBridge';
import SmartOffset from './registry/SmartOffset';
import AuctionHouse from './auction/AuctionHouse';
import ConstructionMode from './construction/ConstructionMode';
import ScopeRouterPanel from './scope/ScopeRouterPanel';
import KMSManager from './compliance/KMSManager';
import ChainOfCustody from './compliance/ChainOfCustody';
import DPIAReport from './compliance/DPIAReport';
import ESealAndPeppol from './sovereign/ESealAndPeppol';
import NationalSync from './sovereign/NationalSync';
import TransparencyPortal from './public/TransparencyPortal';
import ForecastPanel from './forecast/ForecastPanel';
import PortfolioBotPanel from './trading/PortfolioBotPanel';
import Wallet from './did/Wallet';
import LifestyleCard from './identity/LifestyleCard';
import SatelliteView from './satellite/SatelliteView';
import IncidentRootCause from './ai/IncidentRootCause';
import BuildingOptimizer from './ai/BuildingOptimizer';
import ProcurementOptimizer from './ai/ProcurementOptimizer';
import TxDemo from './components/TxDemo';
import AdvisorPanel from './ai/AdvisorPanel';
import { MiniESG } from './routes/miniESG';
import { ConnectorRegistry } from './routes/connectorRegistry';
import { PremiumAnalyticsRoute } from './routes/premiumAnalytics';
import { EvidenceInboxRoute } from './routes/evidenceInbox';
import FundingRadar from './routes/fundingRadar';
import BottomAIPanel from '@/components/BottomAIPanel';
import ContextDrawer from '@/components/ContextDrawer';
import ToastContainer from './components/common/ToastContainer';
import { useAiPanelStore } from '@/store/aiPanelStore';
import { useAIContext } from './store/aiContextStore';
import { useContextDrawerStore } from './store/contextDrawerStore';
import { useEffect } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';

// High-level dashboard assembly; each area is a Sectioned component
export const App = () => {
  const { t } = useTranslation();
  const dark = useThemeStore((s) => s.dark);
  const toggleAi = useAiPanelStore((s) => s.toggle);
  const [view, setView] = useState<string>('dashboard');

  // Always use dark mode - apply theme to HTML element
  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.classList.add('dark');
    htmlElement.classList.remove('light');
    useThemeStore.setState({ dark: true });
  }, []);
  
  // Placeholder data wiring for composition demo
  const baseline = { energyKWh: 10000, fleetLiter: 2000, travelKm: 50000, wasteKg: 3000, paperQty: 400, totalCo2Kg: 0 };
  const optimized = { energyKWh: 8800, fleetLiter: 1640, travelKm: 39000, wasteKg: 2250, paperQty: 400, totalCo2Kg: 0 };
  const activeCompany = useCompanyStore((s) => s.companies.find((c) => c.id === s.activeId) ?? null);
  const can = useAuthStore((s) => s.has);
  
  // Set active panel based on view and auto-open context drawer
  useEffect(() => {
    const panelMap: Record<string, string> = {
      construct: 'Construction',
      ets: 'ETS Bridge',
      oracle: 'Oracle',
      iot: 'IoT Meters',
      govbid: 'Gov Bids',
      futures: 'Futures',
      vol: 'Volatility',
      cbdc: 'CBDC',
      taxonomy: 'Taxonomy Oracle',
      slasher: 'Compliance Slasher',
      regexport: 'Regulator Export',
      tenderscore: 'Tender Scoring',
      smartoffset: 'Smart Offset',
      auction: 'Auction House',
      scoperouter: 'Scope Router',
      scopeexchange: 'Scope Exchange',
      certificatenft: 'Certificate NFT',
      eufabric: 'EU Scope Exchange Fabric',
      evidencechain: 'EU Evidence Chain',
      tendermesh: 'Tender Integrity Mesh',
      tribunal: 'Procurement Tribunal',
      passport: 'ESG Passport Registry',
      subsidy: 'Subsidy Eligibility Engine',
      subsidyallocation: 'Subsidy Allocation',
      beneficial: 'Beneficial Ownership Graph',
      judicial: 'Judicial Trigger Engine',
      satelliteoverlay: 'Satellite Overlay Risk',
      antifraud: 'EU Anti-Fraud Interoperability Layer',
      settlement: 'Settlement Ledger',
      sovereignreconciliation: 'Sovereign Reconciliation',
      kms: 'KMS',
      custody: 'Chain of Custody',
      dpia: 'DPIA',
      eseal: 'eSeal / PEPPOL',
      natsync: 'National Sync',
      portal: 'Transparency',
      forecast: 'Forecast',
      portfolio: 'Portfolio Bot',
      wallet: 'DID Wallet',
      lifestyle: 'Lifestyle',
      sat: 'Satellite',
      incident: 'Incident AI',
      optimizer: 'Building Optimizer',
      procure: 'Procurement AI',
      miniesg: 'MiniESGPanel',
      fundingradar: 'Funding Radar',
    };
    
    const panelName = panelMap[view] || null;
    if (panelName) {
      useAIContext.getState().setActivePanel(panelName);
      
      // Auto-open context drawer for relevant panels
      const relevantPanels = [
        'ETS Bridge', 'Taxonomy Oracle', 'Compliance Slasher', 'Futures',
        'Tender Scoring', 'KMS', 'Chain of Custody', 'DPIA',
        'eSeal / PEPPOL', 'Scope Exchange', 'Certificate NFT', 'EU Scope Exchange Fabric', 'EU Evidence Chain', 'Tender Integrity Mesh', 'Procurement Tribunal', 'ESG Passport Registry', 'Subsidy Eligibility Engine', 'Subsidy Allocation', 'Beneficial Ownership Graph', 'Judicial Trigger Engine', 'Satellite Overlay Risk', 'EU Anti-Fraud Interoperability Layer', 'Settlement Ledger', 'Sovereign Reconciliation', 'National Sync', 'Regulator Export',
        'Portfolio Bot', 'Volatility', 'Auction House',
        'Scope Router', 'Smart Offset', 'MiniESGPanel'
      ];
      if (relevantPanels.includes(panelName)) {
        useContextDrawerStore.getState().openDrawer();
      } else {
        useContextDrawerStore.getState().closeDrawer();
      }
    }

    // Force repaint on navigation to prevent blackout
    window.requestAnimationFrame(() => {
      // Force a repaint
    });
  }, [view]);

  const navItems: NavItem[] = useMemo(() => ([
    { key:'dashboard', label:'Dashboard', group:'General', status:'live' },
    { key:'results', label:'Results', group:'General', status:'live' },
    { key:'aiadvisor', label:'AI Advisor', group:'AI', status:'live' },
    { key:'oracle', label:'Oracle', group:'Data', status:'coming-soon' },
    { key:'iot', label:'IoT Meters', group:'Data', status:'coming-soon' },
    { key:'govbid', label:'Gov Bids', group:'Sovereign', status:'coming-soon' },
    { key:'futures', label:'Futures Desk', group:'Trading', status:'coming-soon' },
    { key:'vol', label:'Volatility', group:'Market', status:'coming-soon' },
    { key:'cbdc', label:'CBDC', group:'Sovereign', status:'coming-soon' },
    { key:'taxonomy', label:'Taxonomy Oracle', group:'Sovereign', status:'coming-soon' },
    { key:'slasher', label:'Compliance Slasher', group:'Sovereign', status:'coming-soon' },
    { key:'regexport', label:'Regulator Export', group:'Sovereign', status:'coming-soon' },
    { key:'tenderscore', label:'Tender Scoring', group:'Sovereign', status:'coming-soon' },
    { key:'ets', label:'ETS Bridge', group:'Sovereign', status:'coming-soon' },
    { key:'smartoffset', label:'Smart Offset', group:'Registry', status:'coming-soon' },
    { key:'auction', label:'Auction House', group:'Market', status:'coming-soon' },
    { key:'construct', label:'Construction', group:'Engineering', status:'coming-soon' },
    { key:'scoperouter', label:'Scope Router', group:'Engineering', status:'coming-soon' },
    { key:'kms', label:'KMS', group:'Compliance', status:'coming-soon' },
    { key:'custody', label:'Chain of Custody', group:'Compliance', status:'coming-soon' },
    { key:'dpia', label:'DPIA', group:'Compliance', status:'coming-soon' },
    { key:'eseal', label:'eSeal / PEPPOL', group:'Sovereign', status:'coming-soon' },
      { key:'scopeexchange', label:'Scope Exchange', group:'Sovereign', status:'coming-soon' },
      { key:'certificatenft', label:'Certificate NFT Issuer', group:'Sovereign', status:'coming-soon' },
      { key:'eufabric', label:'EU Scope Exchange Fabric', group:'Sovereign', status:'coming-soon' },
      { key:'evidencechain', label:'EU Evidence Chain', group:'Sovereign', status:'coming-soon' },
      { key:'tendermesh', label:'Tender Integrity Mesh', group:'Sovereign', status:'live' },
      { key:'fundingradar', label: t('nav.fundingRadar') || 'Funding Radar', group:'Sovereign', status:'live' },
      { key:'tribunal', label:'Procurement Tribunal', group:'Sovereign', status:'coming-soon' },
      { key:'passport', label:'ESG Passport Registry', group:'Sovereign', status:'coming-soon' },
      { key:'subsidy', label:'Subsidy Eligibility Engine', group:'Sovereign', status:'coming-soon' },
      { key:'subsidyallocation', label:'Subsidy Allocation (v2-v3)', group:'Sovereign', status:'coming-soon' },
      { key:'beneficial', label:'Beneficial Ownership Graph', group:'Sovereign', status:'coming-soon' },
      { key:'judicial', label:'Judicial Trigger Engine', group:'Sovereign', status:'coming-soon' },
      { key:'satelliteoverlay', label:'Satellite Overlay Risk', group:'Sovereign', status:'coming-soon' },
      { key:'antifraud', label:'EU Anti-Fraud Interoperability Layer', group:'Sovereign', status:'coming-soon' },
      { key:'settlement', label:'Settlement Ledger', group:'Sovereign', status:'coming-soon' },
      { key:'sovereignreconciliation', label:'Sovereign Reconciliation (v3)', group:'Sovereign', status:'coming-soon' },
      { key:'natsync', label:'National Sync', group:'Sovereign', status:'coming-soon' },
    { key:'portal', label:'Transparency', group:'Public', status:'coming-soon' },
    { key:'forecast', label:'Forecast', group:'Analytics', status:'coming-soon' },
    { key:'premiumanalytics', label:'Premium Analytics', group:'Analytics', status:'live' },
    { key:'connectors', label:'Connector Registry', group:'Marketplace', status:'coming-soon' },
    { key:'evidenceinbox', label:'Evidence Inbox', group:'Marketplace', status:'live' },
    { key:'portfolio', label:'Portfolio Bot', group:'Trading', status:'coming-soon' },
    { key:'wallet', label:'DID Wallet', group:'Identity', status:'coming-soon' },
    { key:'lifestyle', label:'Lifestyle', group:'Identity', status:'coming-soon' },
    { key:'sat', label:'Satellite', group:'Data', status:'coming-soon' },
    { key:'incident', label:'Incident AI', group:'Ops', status:'coming-soon' },
      { key:'optimizer', label:'Building Optimizer', group:'AI', status:'coming-soon' },
      { key:'procure', label:'Procurement AI', group:'AI', status:'coming-soon' },
      { key:'miniesg', label:'EVAT Mini ESG™', group:'General', status:'live' }
  ]), [t]);

  return (
    <div className={`h-screen flex flex-col font-inter ${dark ? 'bg-black text-neutral-100' : 'bg-white text-neutral-900'}`}>
      <HeaderBar
        title={t('title') || 'ESG Simulation Dashboard'}
        right={
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <button onClick={toggleAi} style={{ marginLeft: 8 }} className="px-3 py-1 border border-evat-neon text-evat-neon rounded hover:shadow-neon">
              AI
            </button>
          </div>
        }
      />

      <main className="flex-1 min-h-0 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-[16rem_1fr] gap-6">
        <NavMenu items={navItems} active={view} onSelect={setView} />
        <div className="space-y-6 w-full max-w-6xl">
          <ErrorBoundary>
          {view==='dashboard' && (
            <>
              <Section title={t('inputData') || 'Input Data'}>
                <p className="text-neutral-300">{t('welcomeBody')}</p>
              </Section>
              <Section title={t('license') || 'License Key'}>
                <LicenseKey />
                <div className="mt-3"><CompanyManager /><div className="mt-3"><SSOButton /></div></div>
              </Section>
              <Section title={t('simulation') || 'Simulation'}>
                {can('simulation') ? <PropertyGrid savedCo2Tokens={2500} /> : <div className="text-neutral-500 text-sm">Insufficient permissions.</div>}
              </Section>
            </>
          )}
          {view==='results' && (
            <Section title={t('results') || 'Results'}>
              <ResultsView baseline={baseline} optimized={optimized} companyName={activeCompany?.name} />
              <div className="mt-3">
                <button onClick={() => exportJSON('session', { baseline, optimized, activeCompany })} aria-label="Export session JSON" className="px-4 py-2 rounded border border-evat-gold text-evat-gold hover:shadow-neon">{t('export') || 'Export Data'}</button>
              </div>
            </Section>
          )}
          {view==='oracle' && <Section title="Oracle"><OraclePanel /></Section>}
          {view==='aiadvisor' && <Section title="AI Advisor"><AdvisorPanel baselineTotalKg={120000} optimizedTotalKg={95000} supplierScore={0.7} industryAvgKgPerEmployee={1200} regionEnergyKgPerKWh={0.28} complianceScore={78} /></Section>}
          {view==='iot' && <Section title="IoT Meters"><IoTMeters /></Section>}
          {view==='govbid' && <Section title="Gov Bids"><GovBidPanel /></Section>}
          {view==='futures' && <Section title="Futures"><FuturesDesk /></Section>}
          {view==='vol' && <Section title="Volatility"><VolatilityPanel /></Section>}
          {view==='cbdc' && <Section title="CBDC"><CBDCDesk /></Section>}
          {view==='taxonomy' && <Section title="Taxonomy Oracle"><TaxonomyOraclePanel /></Section>}
          {view==='slasher' && <Section title="Compliance Slasher"><ComplianceSlasherV2 /></Section>}
          {view==='regexport' && <Section title="Regulator Export"><RegulatorExport /></Section>}
          {view==='tenderscore' && <Section title="Tender Scoring"><TenderScoring /></Section>}
          {view==='ets' && <Section title="ETS Bridge"><ETSBridge /></Section>}
          {view==='smartoffset' && <Section title="Smart Offset"><SmartOffsetV2 /></Section>}
          {view==='auction' && <Section title="Auction"><AuctionHouse /></Section>}
          {view==='construct' && <Section title="Construction"><ConstructionMode /></Section>}
          {view==='scoperouter' && <Section title="Scope Router"><ScopeRouterV2 /></Section>}
          {view==='scopeexchange' && <Section title="Scope Exchange"><ScopeExchange /></Section>}
          {view==='certificatenft' && <Section title="Certificate NFT Issuer"><CertificateNFT /></Section>}
          {view==='eufabric' && <Section title="EU Scope Exchange Fabric"><EUFabric /></Section>}
          {view==='evidencechain' && <Section title="EU Evidence Chain"><EvidenceChain /></Section>}
          {view==='tendermesh' && <Section title="Tender Integrity Mesh"><TenderMesh /></Section>}
          {view==='fundingradar' && <Section title="Funding Radar"><FundingRadar /></Section>}
          {view==='tribunal' && <Section title="Procurement Tribunal"><Tribunal /></Section>}
          {view==='passport' && <Section title="ESG Passport Registry"><ESGPassportRegistry /></Section>}
          {view==='subsidy' && <Section title="Subsidy Eligibility Engine"><SubsidyEligibility /></Section>}
          {view==='subsidyallocation' && <Section title="Subsidy Allocation (v2-v3)"><SubsidyAllocation /></Section>}
          {view==='beneficial' && <Section title="Beneficial Ownership Graph"><BeneficialGraph /></Section>}
          {view==='judicial' && <Section title="Judicial Trigger Engine"><JudicialTrigger /></Section>}
          {view==='satelliteoverlay' && <Section title="Satellite Overlay Risk"><SatelliteOverlay /></Section>}
          {view==='antifraud' && <Section title="EU Anti-Fraud Interoperability Layer"><AntiFraud /></Section>}
          {view==='settlement' && <Section title="Settlement Ledger"><SettlementPanel /></Section>}
          {view==='sovereignreconciliation' && <Section title="Sovereign Reconciliation (v3)"><SovereignReconciliation /></Section>}
          {view==='kms' && <Section title="KMS"><KMSManager /></Section>}
          {view==='custody' && <Section title="Chain of Custody"><ChainOfCustody /></Section>}
          {view==='dpia' && <Section title="DPIA"><DPIAReport /></Section>}
          {view==='eseal' && <Section title="eSeal / PEPPOL"><ESeal /></Section>}
          {view==='natsync' && <Section title="National Sync"><NationalSyncV2 /></Section>}
          {view==='portal' && <Section title="Transparency"><TransparencyPortal /></Section>}
          {view==='forecast' && <Section title="Forecast"><ForecastPanel /></Section>}
          {view==='premiumanalytics' && <PremiumAnalyticsRoute />}
          {view==='connectors' && <ConnectorRegistry />}
          {view==='evidenceinbox' && <EvidenceInboxRoute />}
          {view==='portfolio' && <Section title="Portfolio"><PortfolioBotPanel /></Section>}
          {view==='wallet' && <Section title="DID Wallet"><Wallet /></Section>}
          {view==='lifestyle' && <Section title="Lifestyle"><LifestyleCard /></Section>}
          {view==='sat' && <Section title="Satellite"><SatelliteView /></Section>}
          {view==='incident' && <Section title="Incident AI"><IncidentRootCause /></Section>}
          {view==='optimizer' && <Section title="Building Optimizer"><BuildingOptimizer /></Section>}
          {view==='procure' && <Section title="Procurement AI"><ProcurementOptimizer /></Section>}
          {view==='txdemo' && <Section title="Tx Demo"><TxDemo /></Section>}
          {view==='miniesg' && <MiniESG />}
          </ErrorBoundary>
        </div>
      </main>
      
      {/* Floating AI Assistant Panel (v1) */}
      <BottomAIPanel />
      
      {/* Dynamic Context Drawer (v1) */}
      <ContextDrawer />
      
      {/* Toast Notifications */}
      <ToastContainer />
    </div>
  );
};


